import { FeedbackNoteBulkUpload } from "@/__generated__/graphql";
import { Button } from "@/components/ui/button";
import { buildUrl, FEEDBACK_NOTE_CSV_URL } from "@/helpers/url";
import { gql, useMutation } from "@apollo/client";
import { toast } from "sonner";
import { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import * as XLSX from "@e965/xlsx";
import { X } from "lucide-react";
import SelectLocalFolderField from "@/components/common/SelectLocalFolderField/SelectLocalFolderField";
import axios from "axios";
import { SUBCONTRACTING_FOR_USER, TOKEN_KEY } from "@/env";
import { FEEDBACK_NOTE_QUERY } from "@/graphql/queries/FeedbackNotes";

interface CSVInput {
  shotId: string;
  shotName: string;
  service: string;
  version: string;
  note: string;
  noteType?: string;
}

const FEEDBACK_NOTE_BULK_UPLOAD_MUTATION = gql`
  mutation FeedbackNoteBulkUpload($feedbackNotes: [FeedbackNoteInput!]!) {
    feedbackNoteBulkUpload(feedbackNotes: $feedbackNotes) {
      success
      message
    }
  }
`;

const NotesUploadModal = ({
  taskShotIds,
  onRefech,
}: {
  taskShotIds: string[];
  onRefech: () => void;
}) => {
  const [mapClientShotNames] = useMutation<FeedbackNoteBulkUpload>(
    FEEDBACK_NOTE_BULK_UPLOAD_MUTATION,
    {
      refetchQueries: [
        { query: FEEDBACK_NOTE_QUERY, variables: { taskShotIds } },
      ],
    },
  );

  const [shotsData, setShotsData] = useState<CSVInput[]>([]);
  const [fileName, setFileName] = useState<string>();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const acceptedFilesTypes = [".csv", ".xlsx", ".xls", ".ods", ".tsv"];

  const processFile = (binaryStr: string) => {
    const sheets = XLSX.read(binaryStr, { type: "binary" });
    const sheetName = sheets.SheetNames[0];
    const sheet = sheets.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(sheet, {
      raw: false,
      header: ["shotId", "shotName", "service", "version", "noteType", "note"],
    });
    data.shift();
    return data as CSVInput[];
  };

  const readCsv = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = processFile(reader.result as string);
      setShotsData(result);
      setFileName(file.name);
    };
    reader.readAsBinaryString(file);
  };

  const onSubmit = () => {
    const invalidRows = shotsData
      .map((note, index) => (!note.noteType?.trim() ? index + 2 : null)) // +2 for header row in Excel
      .filter(Boolean);

    if (invalidRows.length) {
      toast.error(
        `Please select a Note Type for rows: ${invalidRows.join(", ")}`,
      );
      return;
    }

    mapClientShotNames({
      variables: {
        feedbackNotes: shotsData.map((note) => ({
          ...note,
          noteType: (note.noteType || "").trim(),
        })),
      },
    })
      .then(() => {
        setShowUploadModal(false);
        onRefech();
      })
      .catch((e) => {
        toast.error(e?.message || "Something went wrong");
      });
  };

  return (
    <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
      <DialogTrigger
        className="w-fit cursor-pointer hover:text-service-paint"
        onClick={() => setShowUploadModal(true)}
      >
        Use a spreadsheet to fill this
      </DialogTrigger>

      <DialogContent className="text-sm">
        <div className="text-lg font-medium">Upload feedback</div>
        <Separator />

        {/* Step 1 */}
        <div className="flex flex-col gap-2">
          <div className="font-medium">Step 1:</div>
          <div className="text-secondary-foreground">
            Download this Excel file, select Note type (optional) and add note.
          </div>
          <div
            className="underline text-service-paint font-medium underline-offset-2 cursor-pointer w-fit"
            onClick={async () => {
              try {
                const url = buildUrl(FEEDBACK_NOTE_CSV_URL, {
                  task_shot_ids: taskShotIds.join(","),
                });

                const token = localStorage.getItem(TOKEN_KEY);
                if (!token) {
                  console.error("No token found in localStorage");
                  return;
                }

                const response = await axios.get(url, {
                  responseType: "blob",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "X-Subcontracting-For-User":
                      localStorage.getItem(SUBCONTRACTING_FOR_USER) || "",
                  },
                });

                const blob = new Blob([response.data], {
                  type: response.headers["content-type"]?.toString(),
                });
                const downloadUrl = URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = "FeedbackUploadTemplate.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(downloadUrl);
              } catch (error) {
                toast.error("Download failed");
                console.error(error);
              }
            }}
          >
            Download CSV template
          </div>

          {/* Step 2 */}
          <div className="font-medium mt-4">Step 2:</div>
          <div className="text-secondary-foreground">
            Upload the filled document
          </div>

          {!shotsData.length && (
            <SelectLocalFolderField
              acceptedFilesTypes={acceptedFilesTypes}
              acceptCsvFiles
              onSuccess={(files) => {
                if (files.length) {
                  if (files[0].size > 1000000) {
                    toast.error("File size must be less than 1MB");
                  } else {
                    const extension = files[0].name.split(".").pop();
                    if (acceptedFilesTypes.includes(`.${extension}`)) {
                      readCsv(files[0]);
                    } else {
                      toast.error(
                        `Wrong file type. Please upload a valid file.`,
                      );
                    }
                  }
                } else {
                  toast.error("Please upload a file.");
                }
              }}
            />
          )}

          {!!shotsData.length && (
            <Button
              className="w-fit"
              onClick={() => {
                setShotsData([]);
                setFileName(undefined);
              }}
            >
              {fileName} <X />
            </Button>
          )}
        </div>

        <Separator />

        <div className="flex justify-end gap-4">
          <Button onClick={() => setShowUploadModal(false)}>Close</Button>
          <Button
            className="bg-service-paint hover:bg-service-paint/96 px-2 text-white"
            onClick={onSubmit}
          >
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotesUploadModal;
